/**
 * @fileoverview Renderer for analytics plan tools.
 * Displays timeline-based step visualization.
 */

'use client';

import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface AnalyticsPlanRendererProps {
  /** Whether the tool is loading */
  isLoading: boolean;
  /** Tool output containing steps */
  output?: {
    steps?: Array<{ name: string; description: string }>;
    result?: { steps?: Array<{ name: string; description: string }> };
  };
  /** Icon component to use */
  IconComponent: LucideIcon;
}

/**
 * Renders the create_data_analytics_plan tool with a timeline.
 */
export function AnalyticsPlanRenderer({
  isLoading,
  output,
  IconComponent,
}: AnalyticsPlanRendererProps) {
  if (isLoading) {
    return (
      <>
        <Loader2 className="size-4 animate-spin text-blue-500" />
        <span className="text-muted-foreground">Creating data analytics plan</span>
      </>
    );
  }

  const steps = output?.steps || output?.result?.steps || [];

  return (
    <div className="relative">
      {steps.map((step, index) => (
        <div key={index} className="relative flex gap-3">
          {/* Timeline dot and line */}
          <div className="relative flex flex-col items-center">
            <div>
              <div className="z-10 flex size-6 items-center justify-center rounded-full border-2 border-blue-500 bg-blue-100">
                <IconComponent className="size-3 text-blue-500" />
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className="h-full w-0.5 border-l-2 border-dotted border-gray-300 bg-gray-300" />
            )}
          </div>

          {/* Step content */}
          <div className="min-w-0 flex-1 pb-6">
            <h4 className="mb-1 font-medium text-foreground">{step.name}</h4>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {step.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

interface ExecuteAnalyticsPlanRendererProps {
  /** Whether the tool is loading */
  isLoading: boolean;
  /** Tool output containing results */
  output?: {
    results?: Array<{ success: boolean }>;
  };
}

/**
 * Renders the create_execute_analytics_plan tool with success/failure indicators.
 */
export function ExecuteAnalyticsPlanRenderer({
  isLoading,
  output,
}: ExecuteAnalyticsPlanRendererProps) {
  if (isLoading) {
    return (
      <>
        <Loader2 className="size-4 animate-spin text-blue-500" />
        <span className="text-muted-foreground">Executing analytics plan</span>
      </>
    );
  }

  const results = output?.results || [];

  return (
    <div className="relative">
      {results.map((result, index) => (
        <div key={index} className="relative flex gap-3">
          {/* Success/Failure indicator */}
          <div className="relative flex flex-col items-center">
            <div>
              <div
                className={`z-10 flex size-6 items-center justify-center rounded-full border-2 ${
                  result.success
                    ? 'border-green-500 bg-green-100'
                    : 'border-red-500 bg-red-100'
                }`}
              >
                {result.success ? (
                  <CheckCircle2 className="size-3 text-green-500" />
                ) : (
                  <XCircle className="size-3 text-red-500" />
                )}
              </div>
            </div>
            {/* Timeline connector */}
            {index < results.length - 1 && (
              <div className="h-full w-0.5 border-l-2 border-dotted border-gray-300 bg-gray-300" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
